import React, { useState, useRef, forwardRef, useCallback, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import '../../../styles/PassportWedding.css';
import bookOpeningSound from '../../../assets/sounds/book-opening.mp3';
import ribbonOpenSound from '../../../assets/sounds/Shinning SFX.mp3';
import backgroundMusic from '../../../assets/sounds/Ed Sheeran - Perfect  Piano Cover with Strings (with PIANO SHEET).mp3';
import frontLogoPassport from '../../../assets/images/front-logo-passport-wedding.png';
import logicaLogo from '../../../assets/images/Logica (Black and White).png';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Page component with forwardRef (required by react-pageflip)
const Page = forwardRef(({ children, className = '' }, ref) => {
  return (
    <div className={`book-page ${className}`} ref={ref}>
      {children}
    </div>
  );
});

Page.displayName = 'Page';

const PassportWedding = ({ coupleNames }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState('next');
  const [sparkleEdge, setSparkleEdge] = useState('top-right');
  const [showBoardingPass, setShowBoardingPass] = useState(false);
  const [showRsvp, setShowRsvp] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError] = useState('');
  const [bookReady, setBookReady] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showTapHint, setShowTapHint] = useState(false);
  const [showBoardingPassHint, setShowBoardingPassHint] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [rsvpData, setRsvpData] = useState({
    name: '',
    attending: '',
    guests: '1',
    message: ''
  });
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const bookRef = useRef(null);
  const audioRef = useRef(null);
  const ribbonAudioRef = useRef(null);
  const bgMusicRef = useRef(null);
  const wrapperRef = useRef(null);
  const [sparklePosition, setSparklePosition] = useState({ top: 0, left: 0 });

  // Detect mobile/desktop on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Countdown timer
  useEffect(() => {
    const weddingDate = new Date('May 9, 2026 00:00:00').getTime();
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = weddingDate - now;
      
      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Show boarding pass hint every 5 seconds while boarding pass is visible
  useEffect(() => {
    let hintInterval;
    let hideTimeout;
    
    if (showBoardingPass) {
      // Show hint immediately when boarding pass appears
      setShowBoardingPassHint(true);
      
      // Hide after 3 seconds
      hideTimeout = setTimeout(() => {
        setShowBoardingPassHint(false);
      }, 3000);
      
      // Then show every 5 seconds
      hintInterval = setInterval(() => {
        setShowBoardingPassHint(true);
        setTimeout(() => {
          setShowBoardingPassHint(false);
        }, 3000);
      }, 8000); // 5 seconds hidden + 3 seconds shown = 8 seconds cycle
    } else {
      setShowBoardingPassHint(false);
    }
    
    return () => {
      if (hintInterval) clearInterval(hintInterval);
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, [showBoardingPass]);

  // Preload audio on mount for instant playback
  useEffect(() => {
    // Page turn sound - higher volume and optimized for instant play
    const pageAudio = new Audio(bookOpeningSound);
    pageAudio.volume = 0.7;
    pageAudio.preload = 'auto';
    pageAudio.load();
    // Pre-play and pause to prime the audio buffer (eliminates delay)
    pageAudio.play().then(() => {
      pageAudio.pause();
      pageAudio.currentTime = 0;
    }).catch(() => {});
    audioRef.current = pageAudio;
    
    // Ribbon opening sound (shinning effect)
    const ribbonAudio = new Audio(ribbonOpenSound);
    ribbonAudio.volume = 0.5;
    ribbonAudio.preload = 'auto';
    ribbonAudio.load();
    // Pre-play and pause to prime the audio buffer
    ribbonAudio.play().then(() => {
      ribbonAudio.pause();
      ribbonAudio.currentTime = 0;
    }).catch(() => {});
    ribbonAudioRef.current = ribbonAudio;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (ribbonAudioRef.current) {
        ribbonAudioRef.current.pause();
        ribbonAudioRef.current = null;
      }
    };
  }, []);

  // Initialize background music
  useEffect(() => {
    // Using local background music file
    const bgMusic = new Audio(backgroundMusic);
    bgMusic.volume = 0.3;
    bgMusic.loop = true; // Enable native looping
    bgMusic.preload = 'auto';
    
    // Set start time after audio is loaded
    const handleLoaded = () => {
      bgMusic.currentTime = 5; // Skip first 5 seconds
    };
    bgMusic.addEventListener('loadedmetadata', handleLoaded);
    
    // Handle timeupdate to ensure we skip intro on loop
    const handleTimeUpdate = () => {
      if (bgMusic.currentTime < 5) {
        bgMusic.currentTime = 5;
      }
    };
    bgMusic.addEventListener('timeupdate', handleTimeUpdate);
    
    // Handle any errors
    const handleError = (e) => {
      console.log('Audio error:', e);
    };
    bgMusic.addEventListener('error', handleError);
    
    bgMusicRef.current = bgMusic;

    return () => {
      if (bgMusicRef.current) {
        bgMusicRef.current.removeEventListener('loadedmetadata', handleLoaded);
        bgMusicRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        bgMusicRef.current.removeEventListener('error', handleError);
        bgMusicRef.current.pause();
        bgMusicRef.current = null;
      }
    };
  }, []);

  // Toggle background music
  const toggleMusic = useCallback(() => {
    if (bgMusicRef.current) {
      if (isMusicPlaying) {
        bgMusicRef.current.pause();
      } else {
        bgMusicRef.current.play().catch(() => {
          console.log('Music autoplay blocked by browser');
        });
      }
      setIsMusicPlaying(!isMusicPlaying);
    }
  }, [isMusicPlaying]);

  // Show tap hint after 3 seconds (only if not opened)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen && !isOpening) {
        setShowTapHint(true);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);

  // Hide tap hint when opening
  useEffect(() => {
    if (isOpening || isOpen) {
      setShowTapHint(false);
    }
  }, [isOpening, isOpen]);

  // Trigger resize event when book opens to force react-pageflip to recalculate
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        setBookReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setBookReady(false);
    }
  }, [isOpen]);

  // Play ribbon opening sound with sparkle effect
  const playRibbonSound = useCallback(() => {
    if (ribbonAudioRef.current) {
      ribbonAudioRef.current.currentTime = 0;
      ribbonAudioRef.current.play().catch(() => {});
    }
  }, []);

  // Handle opening animation
  const handleOpen = () => {
    if (!isOpening && !isOpen) {
      setIsOpening(true);
      
      // Play the shinning sound effect immediately when ribbon starts unwrapping
      playRibbonSound();
      
      setTimeout(() => {
        setIsOpen(true);
        setTimeout(() => {
          setShowBoardingPass(true);
        }, 600);
      }, 1200);
    }
  };

  // Handle close
  const handleClose = () => {
    setShowBoardingPass(false);
    // Reset the book to first page before closing
    if (bookRef.current) {
      bookRef.current.pageFlip().flip(0);
    }
    setTimeout(() => {
      setCurrentPage(0);
      setIsOpen(false);
      setIsOpening(false);
    }, 400);
  };

  // Play page turn sound instantly
  const playPageSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  // Calculate sparkle position based on the actual book element
  const calculateSparklePosition = useCallback((edge) => {
    if (!wrapperRef.current) return;
    
    const wrapper = wrapperRef.current;
    const bookElement = wrapper.querySelector('.stf__parent');
    
    if (!bookElement) return;
    
    const wrapperRect = wrapper.getBoundingClientRect();
    const bookRect = bookElement.getBoundingClientRect();
    
    // Calculate book position relative to wrapper
    const bookLeft = bookRect.left - wrapperRect.left;
    const bookTop = bookRect.top - wrapperRect.top;
    const bookRight = bookLeft + bookRect.width;
    const bookBottom = bookTop + bookRect.height;
    
    let position = { top: 0, left: 0 };
    
    switch (edge) {
      case 'top-right':
        position = { top: bookTop - 20, left: bookRight - 60 };
        break;
      case 'top-left':
        position = { top: bookTop - 20, left: bookLeft - 20 };
        break;
      case 'bottom-right':
        position = { top: bookBottom - 60, left: bookRight - 60 };
        break;
      case 'bottom-left':
        position = { top: bookBottom - 60, left: bookLeft - 20 };
        break;
      default:
        position = { top: bookTop - 20, left: bookRight - 60 };
    }
    
    setSparklePosition(position);
  }, []);

  // Page flip handlers
  const onFlip = useCallback((e) => {
    const newPage = e.data;
    const edges = ['top-right', 'top-left', 'bottom-right', 'bottom-left'];
    const randomEdge = edges[Math.floor(Math.random() * edges.length)];
    
    setFlipDirection(newPage > currentPage ? 'next' : 'prev');
    setSparkleEdge(randomEdge);
    calculateSparklePosition(randomEdge);
    setIsFlipping(true);
    setCurrentPage(newPage);
    playPageSound();
    
    // Reset flipping state after animation completes
    setTimeout(() => {
      setIsFlipping(false);
    }, 700);
  }, [playPageSound, currentPage, calculateSparklePosition]);

  const flipNext = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipNext();
    }
  };

  const flipPrev = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipPrev();
    }
  };

  const flipToPage = (pageIndex) => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flip(pageIndex);
    }
  };

  // Total pages count
  const totalPages = 9;

  // Parse couple names
  const formatCoupleNames = (names) => {
    if (!names) return 'Bride & Groom';
    return names
      .split('-')
      .map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
      .join(' & ');
  };

  const getFirstName = (names, index) => {
    if (!names) return index === 0 ? 'John' : 'Jane';
    const parts = names.split('-');
    return parts[index] ? parts[index].charAt(0).toUpperCase() + parts[index].slice(1).toLowerCase() : (index === 0 ? 'John' : 'Jane');
  };

  // Invitation data
  const invitationData = {
    coupleNames: 'John & Kristenz',
    groomName: 'John',
    brideName: 'Kristenz',
    groomFullName: 'John Ronald T. Robillos',
    brideFullName: 'Kristenz C. Pejana',
    weddingDate: 'May 9, 2026',
    weddingDay: 'Saturday',
    ceremonyTime: '10:00 AM',
    receptionTime: '12:00 NN',
    churchName: "Daluyan Community Resource Center",
    churchLocation: "Sitio Acacia, Brgy. Malainen Bago, Naic",
    receptionVenue: "Daluyan Community Resource Center",
    receptionLocation: "Naic, Philippines",
    dressCode: 'Formal / Semi-Formal Attire',
    bibleVerse: '"When the time is right, I, the LORD, will make it happen."',
    bibleRef: 'Isaiah 60:22',
    groomParents: {
      father: 'Mr. Itzhak Kolangi',
      mother: 'Mrs. Marilyn Kolangi'
    },
    brideParents: {
      father: 'Mr. Roderick Pejana',
      mother: 'Mrs. Alexandria Pejana'
    },
    hashtag: '#JOHNeyToForeverWithKristenz',
    hashtag2: '#aKRISTENZclearLoveWithJohn',
    // Love Story Timeline
    loveStory: [
      {
        year: '2018',
        title: 'First Met',
        description: 'We first crossed paths at a friend\'s birthday party.',
        icon: '👋'
      },
      {
        year: '2019',
        title: 'First Date',
        description: 'Our first official date at a coffee shop.',
        icon: '☕'
      },
      {
        year: '2021',
        title: 'Said "I Love You"',
        description: 'Three words that changed everything.',
        icon: '💕'
      },
      {
        year: '2023',
        title: 'The Proposal',
        description: 'He asked, she said YES!',
        icon: '💍'
      },
      {
        year: '2024',
        title: 'Wedding Day',
        description: 'The beginning of forever.',
        icon: '💒'
      }
    ],
    // Photo Gallery
    photos: [
      { id: 1, caption: 'Our first photo together' },
      { id: 2, caption: 'Beach getaway' },
      { id: 3, caption: 'Christmas celebration' },
      { id: 4, caption: 'The proposal day' },
      { id: 5, caption: 'Engagement shoot' },
      { id: 6, caption: 'Pre-wedding adventure' }
    ],
    // Entourage
    entourage: {
      // Principal Sponsors (God Father / God Mother)
      principalSponsors: [
        { groom: 'Alvin Abarquez', bride: 'Sheilamae Abarquez' },
        { groom: 'Christopher Cruz', bride: 'Maureen Cruz' },
        { groom: 'Michael Angelo Cruz', bride: 'Hermaly Lazo' },
        { groom: 'Elias Padilla Jr.', bride: 'Rubileen Cruz' },
        { groom: 'Natdemzon Pagkanlungan', bride: 'Saraquil Cruz' },
        { groom: 'Owen Bituin', bride: 'Jessica Estole' },
        { groom: 'Raymund Villanueva', bride: 'Mika Villanueva' },
        { groom: 'Rommel Angga', bride: 'Marites Baylon' },
        { groom: 'Alvin Almazan', bride: 'Zephania Almazan' },
        { groom: 'Edwin Lara', bride: 'Almira Lara' },
        { groom: 'Angelito Viado', bride: 'Marilyn Raymundo' },
        { groom: 'Sernan Ganibo', bride: 'Khit Herrera' },
        { groom: 'Maswee Perez', bride: 'Marilyn De Ocampo' },
        { groom: 'Oscar Arada', bride: 'Nora Arada' },
        { groom: 'Asterino Robillos', bride: 'Rachell Paganingan' }
      ],
      // Wedding party & secondary sponsors (next page)
      bestMan: 'Tito Directo',
      maidOfHonor: 'Eanna Aaliyah Cruz',
      groomsmen: [
        'Christian Tablada',
        'Allen Tumbagahan',
        'Redrick Pejana',
        'Andrei Lazo',
        'Vercon Cruz',
        'Jayson Robillos',
        'Jim Noel Tablada',
        'Joichilo Funabashi',
        'Jeffrey Tablada'
      ],
      bridesmaids: [
        'Lyka Directo',
        'Krissha Pejana',
        'Claudine Espinosa',
        'Cherry Mae Laag',
        'Issey Lazo',
        'Janna Robillos',
        'Camille Bituin',
        'Charlene Baldovi',
        'CJhay Aguilar'
      ],
      // Special secondary sponsors
      lightenOurPath: ['Angelito Ligaya', 'Khariza Joy Partoza'],
      clotheUsAsOne: ['Aldron Paulo Rañeses', 'Kayrish Rañeses'],
      ringBearer: 'Little John',
      coinBearer: 'Little Mark',
      flowerGirl: 'Little Maria',
      bibleBearers: ['Mark Santos', 'Luke Reyes'],
      littleGroom: 'Baby James',
      littleBride: 'Baby Sofia',
      cordSponsors: { groom: 'Mr. & Mrs. Villanueva', bride: 'Mr. & Mrs. Fernandez' },
      veilSponsors: { groom: 'Mr. & Mrs. Dela Cruz', bride: 'Mr. & Mrs. Bautista' },
      candleSponsors: { groom: 'Mr. & Mrs. Aquino', bride: 'Mr. & Mrs. Pascual' }
    }
  };

  const handleRsvpChange = (e) => {
    const { name, value } = e.target;
    setRsvpData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing again
    if (rsvpError) setRsvpError('');
  };

  const handleRsvpSubmit = async (e) => {
    e.preventDefault();
    setRsvpError('');
    setRsvpLoading(true);

    try {
      const payload = {
        ...rsvpData,
        invitationId: 'passport-wedding-john-kristenz',
        coupleNames: invitationData.coupleNames
      };

      const resp = await fetch(`${API_BASE_URL}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(data.message || 'Failed to submit RSVP. Please try again.');
      }

      console.log('RSVP submitted successfully:', data);
      setRsvpSubmitted(true);
    } catch (err) {
      console.error('RSVP submission error:', err);
      setRsvpError(err.message || 'Failed to submit RSVP. Please try again.');
    } finally {
      setRsvpLoading(false);
    }
  };

  return (
    <div className="passport-invitation">
      <div className="passport-bg-pattern"></div>

      {/* Music Toggle Button */}
      <button 
        className={`music-toggle-btn ${isMusicPlaying ? 'playing' : ''}`} 
        onClick={toggleMusic}
        aria-label={isMusicPlaying ? 'Mute music' : 'Play music'}
      >
        <span className="music-icon">
          {isMusicPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
          )}
        </span>
        <span className="music-waves">
          <span className="wave"></span>
          <span className="wave"></span>
          <span className="wave"></span>
        </span>
      </button>

      {/* Sparkles */}
      {isOpening && (
        <div className="sparkle-container">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`sparkle sparkle-${i + 1}`}>✦</div>
          ))}
        </div>
      )}

      {/* Close Button - hidden when boarding pass is shown */}
      {isOpen && !showBoardingPass && (
        <button className="close-passport-btn" onClick={handleClose}>
          <span className="close-icon">✕</span>
          <span className="close-text">Close</span>
        </button>
      )}

      {/* Main Passport */}
      <div className={`passport-wrapper ${isOpen ? 'is-open' : ''}`}>
        <div className={`passport-container ${isOpening ? 'opening' : ''} ${isOpen ? 'opened' : ''}`}>
          
          {/* Ribbon Decoration */}
          <div className={`ribbon-wrapper ${isOpening ? 'unwrapping' : ''}`}>
            <div className="ribbon ribbon-horizontal">
              <div className="ribbon-shine"></div>
            </div>
            <div className="ribbon ribbon-vertical">
              <div className="ribbon-shine"></div>
            </div>
            <div className="ribbon-bow">
              <div className="bow-left"></div>
              <div className="bow-right"></div>
              <div className="bow-knot"></div>
              <div className="bow-tail-left"></div>
              <div className="bow-tail-right"></div>
            </div>
            <div className="wax-seal">
              <div className="seal-inner">
                <span className="seal-initials">
                  {invitationData.groomName.charAt(0)}
                  <span className="seal-heart">♥</span>
                  {invitationData.brideName.charAt(0)}
                </span>
              </div>
              <div className="seal-crack seal-crack-1"></div>
              <div className="seal-crack seal-crack-2"></div>
              <div className="seal-crack seal-crack-3"></div>
            </div>
          </div>

          {/* Front Cover */}
          <div className="passport-cover" onClick={handleOpen}>
            <div className="passport-cover-content">
              <div className="passport-header-text">Wedding</div>
              <h1 className="passport-title">PASSPORT</h1>
              
              <div className="passport-emblem">
                <img src={frontLogoPassport} alt="Wedding Emblem" className="emblem-logo" />
              </div>

              <div className="passport-save-date">
                <span>SAVE OUR DATE</span>
              </div>

              <div className="passport-couple-info">
                <p className="to-marriage">TO THE MARRIAGE OF</p>
                <h2 className="couple-names-cover">{invitationData.coupleNames}</h2>
                <p className="wedding-date-cover">05.9.26</p>
              </div>
            </div>
          </div>

          {/* Hand gesture tap hint - shows after 3 seconds, positioned over wax seal */}
          {showTapHint && !isOpen && !isOpening && (
            <div className="tap-hint-gesture" onClick={handleOpen}>
              <div className="hand-icon">👆</div>
              <div className="tap-rings">
                <div className="tap-ring ring-1"></div>
                <div className="tap-ring ring-2"></div>
                <div className="tap-ring ring-3"></div>
              </div>
            </div>
          )}

          {/* Back Cover - becomes LEFT page when opened */}
          <div className="passport-back-cover">
            <div className="back-cover-content">
              <div className="bible-verse">
                <p className="verse-text">{invitationData.bibleVerse}</p>
                <p className="verse-ref">{invitationData.bibleRef}</p>
              </div>
              <div className="airplane-animation">
                <div className="plane-trail"></div>
                <div className="plane-icon">✈</div>
                <div className="heart-start">💕</div>
              </div>
            </div>
          </div>

          {/* Open Book Layout - Shows when opened */}
          <div className={`open-book ${isOpen ? 'visible' : ''}`}>
            {/* Page Navigation */}
            <div className="page-navigation">
              <button 
                className={`nav-btn prev ${currentPage === 0 ? 'disabled' : ''}`}
                onClick={flipPrev}
                disabled={currentPage === 0}
              >
                ‹
              </button>
              <div className="page-numbers">
                <span className="current-page">{currentPage + 1}</span>
                <span className="page-separator">/</span>
                <span className="total-pages">{totalPages}</span>
              </div>
              <button 
                className={`nav-btn next ${currentPage >= totalPages - 1 ? 'disabled' : ''}`}
                onClick={flipNext}
                disabled={currentPage >= totalPages - 1}
              >
                ›
              </button>
            </div>

            {/* Book Container with react-pageflip */}
            <div className={`book-container ${bookReady ? 'ready' : ''} ${isMobile ? 'mobile' : 'desktop'}`}>
              <div ref={wrapperRef} className={`flipbook-wrapper ${isFlipping ? 'flipping' : ''} ${sparkleEdge}`}>
                {/* Page turn sparkle effect */}
                <div 
                  className={`page-turn-sparkles ${isFlipping ? 'active' : ''} ${sparkleEdge}`}
                  style={{ top: sparklePosition.top, left: sparklePosition.left }}
                >
                  <span className="sparkle sparkle-1">✦</span>
                  <span className="sparkle sparkle-2">✧</span>
                  <span className="sparkle sparkle-3">✦</span>
                  <span className="sparkle sparkle-4">⋆</span>
                  <span className="sparkle sparkle-5">✧</span>
                  <span className="sparkle sparkle-6">✦</span>
                  <span className="sparkle sparkle-7">⋆</span>
                  <span className="sparkle sparkle-8">✧</span>
                </div>
                <HTMLFlipBook
                  ref={bookRef}
                  width={isMobile ? 360 : 380}
                  height={isMobile ? 580 : 540}
                  size="stretch"
                  minWidth={isMobile ? 320 : 280}
                  maxWidth={isMobile ? 400 : 450}
                  minHeight={isMobile ? 520 : 400}
                  maxHeight={isMobile ? 640 : 620}
                  drawShadow={true}
                  flippingTime={600}
                  usePortrait={isMobile}
                  startZIndex={0}
                  autoSize={false}
                  maxShadowOpacity={0.5}
                  showCover={true}
                  mobileScrollSupport={false}
                  swipeDistance={20}
                  clickEventForward={true}
                  useMouseEvents={true}
                  className={`passport-flipbook ${isMobile ? 'portrait-mode' : ''}`}
                  onFlip={onFlip}
                  style={{ margin: '0 auto' }}
                >
                {/* Page 1 - Back Cover Inner / Bible Verse */}
                <Page className="page-green">
                  <div className="back-cover-inner">
                    <div className="bible-verse">
                      <div className="plane-icon-small">✈️</div>
                      <p className="verse-text">{invitationData.bibleVerse}</p>
                      <p className="verse-ref">{invitationData.bibleRef}</p>
                    </div>
                  </div>
                </Page>

                {/* Page 2 - Parents & Invitation */}
                <Page className="page-cream">
                  <div className="page-content">
                    <div className="parents-section">
                      <p className="parents-intro">THE PRESENCE OF OUR LORD JESUS CHRIST</p>
                      <p className="parents-intro">AND TOGETHER WITH OUR BELOVED PARENTS</p>
                      
                      <div className="parents-names">
                        <div className="parent-group">
                          <p className="parent-name">{invitationData.groomParents.father}</p>
                          <p className="parent-name">{invitationData.groomParents.mother}</p>
                        </div>
                        <div className="parent-group">
                          <p className="parent-name">{invitationData.brideParents.father}</p>
                          <p className="parent-name">{invitationData.brideParents.mother}</p>
                        </div>
                      </div>
                    </div>

                    <div className="couple-section">
                      <p className="we-text">WE,</p>
                      <h2 className="couple-script">{invitationData.groomName}</h2>
                      <p className="and-text">&</p>
                      <h2 className="couple-script">{invitationData.brideName}</h2>
                      <p className="invite-text">JOYFULLY INVITE YOU TO WITNESS</p>
                      <p className="invite-text">THE UNITING OF OUR LIVES AS ONE</p>
                    </div>

                    <div className="passport-info-section">
                      <h3 className="passport-to">PASSPORT TO MARRIAGE</h3>
                      <div className="passport-details-grid">
                        <div className="detail-row">
                          <span className="detail-label">Type</span>
                          <span className="detail-value">WEDDING</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Groom's Name</span>
                          <span className="detail-value">{invitationData.groomFullName}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Bride's Name</span>
                          <span className="detail-value">{invitationData.brideFullName}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Destination</span>
                          <span className="detail-value">{invitationData.churchName}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Date</span>
                          <span className="detail-value">{invitationData.weddingDate}</span>
                        </div>
                      </div>
                      <div className="couple-seal">
                        <span>{invitationData.groomName.charAt(0)}</span>
                        <span className="seal-and">&</span>
                        <span>{invitationData.brideName.charAt(0)}</span>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* Page 3 - Snap & Share + Gift Guide + RSVP */}
                <Page className="page-cream">
                  <div className="page-content">
                    <div className="snap-share-section compact">
                      <h3 className="section-title script">Snap and Share</h3>
                      <p className="snap-text">SHARE SOME SNAPS ON SOCIAL MEDIA</p>
                      <p className="snap-text">BY USING THE HASHTAGS</p>
                      <p className="hashtag">{invitationData.hashtag}</p>
                    </div>

                    <div className="gift-section compact">
                      <h3 className="section-title script">Gift Guide</h3>
                      <p className="gift-text">Your presence at our wedding is the greatest gift of all.</p>
                      <p className="gift-text">However, should you wish to honor us with a gift,</p>
                      <p className="gift-text">a contribution toward our new beginning</p>
                      <p className="gift-text">would be most sincerely appreciated.</p>
                    </div>

                    <div className="rsvp-info-section compact">
                      <h3 className="section-title script">RSVP</h3>
                      <p className="rsvp-note">KINDLY RESPOND BEFORE May 1, 2026</p>
                      <button 
                        className="rsvp-page-btn"
                        onClick={(e) => { e.stopPropagation(); setShowRsvp(true); }}
                      >
                        RSVP Online
                      </button>
                    </div>
                  </div>
                </Page>

                {/* Page 4 - Attire */}
                <Page className="page-cream">
                  <div className="page-content">
                    <div className="attire-section">
                      <h3 className="section-title script">Attire</h3>
                      <p className="attire-text">PLEASE JOIN US BY WEARING THE</p>
                      <p className="attire-text">COLORS OF OUR DAY</p>
                      <div className="attire-colors">
                        <div className="color-swatch" style={{backgroundColor: '#F5F5DC'}} title="Beige">
                          <span className="color-name">Beige</span>
                        </div>
                        <div className="color-swatch" style={{backgroundColor: '#FFFDD0'}} title="Cream">
                          <span className="color-name">Cream</span>
                        </div>
                        <div className="color-swatch" style={{backgroundColor: '#FFFFFF', border: '1px solid #ddd'}} title="White">
                          <span className="color-name">White</span>
                        </div>
                        <div className="color-swatch" style={{backgroundColor: '#B4C7D9'}} title="Dusty Blue">
                          <span className="color-name">Dusty Blue</span>
                        </div>
                      </div>
                      <div className="attire-icons">
                        <span className="attire-icon">👔</span>
                        <span className="attire-type">FORMAL / SEMI-FORMAL ATTIRE</span>
                        <span className="attire-icon">👗</span>
                      </div>
                      
                      {/* Sponsors Attire */}
                      <div className="sponsors-attire">
                        <div className="sponsor-attire-item">
                          <span className="sponsor-attire-label">GodMother</span>
                          <span className="sponsor-attire-desc">Beige or Cream Formal Gown</span>
                        </div>
                        <div className="sponsor-attire-item">
                          <span className="sponsor-attire-label">GodFather</span>
                          <span className="sponsor-attire-desc">Beige Barong & Black Pants</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* Page 5 - Entourage Part 1: Principal Sponsors */}
                <Page className="page-cream">
                  <div className="page-content">
                    <div className="entourage-section">
                      <h3 className="section-title script">The Entourage</h3>
                      
                      <div className="entourage-group principal-sponsors">
                        <h4 className="entourage-title">Principal Sponsors</h4>
                        <div className="sponsors-list">
                          <div className="sponsor-pair sponsor-headers">
                            <span className="sponsor-header">God Father</span>
                            <span className="sponsor-header">God Mother</span>
                          </div>
                          {invitationData.entourage.principalSponsors.map((sponsor, index) => (
                            <div key={index} className="sponsor-pair">
                              <span className="sponsor-name">{sponsor.groom}</span>
                              <span className="sponsor-name">{sponsor.bride}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* Page 6 - Entourage Part 2: Wedding Party & Secondary Sponsors */}
                <Page className="page-cream">
                  <div className="page-content">
                    <div className="entourage-section">
                      <h3 className="section-title script">Secondary Sponsors</h3>

                      {/* Best Man & Maid of Honor */}
                      <div className="entourage-group wedding-party">
                        <div className="party-column">
                          <h4 className="entourage-subtitle">Best Man</h4>
                          <p className="party-name">{invitationData.entourage.bestMan}</p>
                        </div>
                        <div className="party-column">
                          <h4 className="entourage-subtitle">Maid of Honor</h4>
                          <p className="party-name">{invitationData.entourage.maidOfHonor}</p>
                        </div>
                      </div>

                      {/* Groomsmen & Bridesmaids */}
                      <div className="entourage-group wedding-party">
                        <div className="party-column">
                          <h4 className="entourage-subtitle">Groomsmen</h4>
                          {invitationData.entourage.groomsmen.map((name, index) => (
                            <p key={index} className="party-name">{name}</p>
                          ))}
                        </div>
                        <div className="party-column">
                          <h4 className="entourage-subtitle">Bridesmaids</h4>
                          {invitationData.entourage.bridesmaids.map((name, index) => (
                            <p key={index} className="party-name">{name}</p>
                          ))}
                        </div>
                      </div>

                      {/* Special Secondary Sponsors */}
                      <div className="entourage-group secondary-sponsors">
                        <div className="sponsor-type">
                          <h4 className="entourage-subtitle">To Lighten Our Path</h4>
                          {invitationData.entourage.lightenOurPath.map((name, index) => (
                            <p key={index} className="sponsor-name">{name}</p>
                          ))}
                        </div>
                        <div className="sponsor-type">
                          <h4 className="entourage-subtitle">To Clothe Us As One</h4>
                          {invitationData.entourage.clotheUsAsOne.map((name, index) => (
                            <p key={index} className="sponsor-name">{name}</p>
                          ))}
                        </div>
                      </div>

                      {/* To Bind Us Together */}
                      <div className="entourage-group bind-together">
                        <h4 className="entourage-subtitle">To Bind Us Together</h4>
                        <p className="sponsor-name">Aldreck Pejana</p>
                        <p className="sponsor-name">Joice Ann Robillos</p>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* Page 7 - Entourage Part 3: Kids & Bearers */}
                <Page className="page-cream">
                  <div className="page-content">
                    <div className="entourage-section">
                      {/* Ring Bearer & Coin Bearer - side by side */}
                      <div className="entourage-group bearers-section">
                        <div className="bearer-item">
                          <h4 className="entourage-subtitle">Ring Bearer</h4>
                          <p className="party-name">Gerardo Dela Cruz 3rd</p>
                          <span className="bearer-icon">💍</span>
                        </div>
                        <div className="bearer-item">
                          <h4 className="entourage-subtitle">Coin Bearer</h4>
                          <p className="party-name">Ian Greyson Pon-an</p>
                          <span className="bearer-icon">🪙</span>
                        </div>
                      </div>

                      {/* Bible Bearer */}
                      <div className="entourage-group bible-bearers">
                        <h4 className="entourage-subtitle">Bible Bearer</h4>
                        <p className="party-name">Caleb James Partoza</p>
                        <span className="bearer-icon">📖</span>
                      </div>

                      {/* Flower Girls */}
                      <div className="entourage-group flower-girls-section">
                        <h4 className="entourage-subtitle">Flower Girls</h4>
                        <p className="party-name">Joyvelle Buenaventura</p>
                        <p className="party-name">Kaylin Ann Dela Cruz</p>
                        <p className="party-name">Zoey Cuevas</p>
                        <p className="party-name">Aeris Rañeses</p>
                        <p className="party-name">Paula Rañeses</p>
                        <span className="bearer-icon">🌸</span>
                      </div>

                      {/* Little Groom & Little Bride */}
                      <div className="entourage-group bearers-section little-couple">
                        <div className="bearer-item">
                          <h4 className="entourage-subtitle">Little Groom</h4>
                          <p className="party-name">Jan Kadence Robillos</p>
                          <span className="bearer-icon">🤵</span>
                        </div>
                        <div className="bearer-item">
                          <h4 className="entourage-subtitle">Little Bride</h4>
                          <p className="party-name">Janina Khandice Robillos</p>
                          <span className="bearer-icon">👰</span>
                        </div>
                      </div>

                      <div className="entourage-footer">
                        <p className="entourage-thanks">With love and gratitude,</p>
                        <p className="entourage-couple">{invitationData.groomName} & {invitationData.brideName}</p>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* Page 8 - Location & Countdown */}
                <Page className="page-cream">
                  <div className="page-content page-8-content">
                    {/* Countdown Section */}
                    <div className="countdown-section compact">
                      <h3 className="countdown-title">Counting Down To Forever</h3>
                      <div className="countdown-timer">
                        <div className="countdown-item">
                          <span className="countdown-number">{countdown.days}</span>
                          <span className="countdown-label">Days</span>
                        </div>
                        <div className="countdown-separator">:</div>
                        <div className="countdown-item">
                          <span className="countdown-number">{countdown.hours}</span>
                          <span className="countdown-label">Hrs</span>
                        </div>
                        <div className="countdown-separator">:</div>
                        <div className="countdown-item">
                          <span className="countdown-number">{countdown.minutes}</span>
                          <span className="countdown-label">Min</span>
                        </div>
                        <div className="countdown-separator">:</div>
                        <div className="countdown-item">
                          <span className="countdown-number">{countdown.seconds}</span>
                          <span className="countdown-label">Sec</span>
                        </div>
                      </div>
                    </div>

                    {/* Location Section with QR */}
                    <div className="map-section with-qr">
                      <h3 className="section-title">📍 Location</h3>
                      <p className="venue-name-header">Daluyan Community Resource Center</p>
                      <p className="venue-address-sub">Sitio Acacia, Brgy. Malainen Bago, Naic</p>
                      <div className="location-actions">
                        <a 
                          href="https://maps.app.goo.gl/jJxfqcvMQzZbYwXR8" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="map-link-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>📍</span> Open Maps
                        </a>
                        <div className="qr-code-mini">
                          <a 
                            href="https://maps.app.goo.gl/jJxfqcvMQzZbYwXR8"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <img 
                              src="https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=https://maps.app.goo.gl/jJxfqcvMQzZbYwXR8&bgcolor=f8fafb&color=4a6b7a"
                              alt="Scan for location"
                              className="qr-mini-img"
                            />
                          </a>
                          <span className="qr-mini-label">Scan</span>
                        </div>
                      </div>
                    </div>

                    {/* Add to Calendar Section */}
                    <div className="calendar-section compact">
                      <h3 className="section-title">📅 Save the Date</h3>
                      <div className="calendar-buttons">
                        <a 
                          href="https://calendar.google.com/calendar/render?action=TEMPLATE&text=John%20Ronald%20%26%20Kristenz%20Wedding&dates=20260410T020000Z/20260410T100000Z&details=Join%20us%20as%20we%20celebrate%20our%20love%20and%20union.%0A%0ACeremony%3A%2010%3A00%20AM%0AReception%3A%2012%3A00%20NN%0ADress%20Code%3A%20Formal%20%2F%20Semi-Formal&location=Daluyan%20Community%20Resource%20Center%2C%20Sitio%20Acacia%2C%20Brgy.%20Malainen%20Bago%2C%20Naic%2C%20Philippines"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="calendar-btn google-cal"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="cal-icon">📆</span>
                          <span>Google</span>
                        </a>
                        <a 
                          href="data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VEVENT%0ADTSTART:20260410T100000%0ADTEND:20260410T180000%0ASUMMARY:John%20Ronald%20%26%20Kristenz%20Wedding%0ADESCRIPTION:Join%20us%20as%20we%20celebrate%20our%20love%20and%20union.%5CnCeremony%3A%2010%3A00%20AM%5CnReception%3A%2012%3A00%20NN%5CnDress%20Code%3A%20Formal%20%2F%20Semi-Formal%0ALOCATION:Daluyan%20Community%20Resource%20Center%2C%20Sitio%20Acacia%2C%20Brgy.%20Malainen%20Bago%2C%20Naic%2C%20Philippines%0AEND:VEVENT%0AEND:VCALENDAR"
                          download="wedding-invitation.ics"
                          className="calendar-btn apple-cal"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="cal-icon">🍎</span>
                          <span>Apple</span>
                        </a>
                        <a 
                          href="https://outlook.live.com/calendar/0/action/compose?subject=John%20Ronald%20%26%20Kristenz%20Wedding&startdt=2026-04-10T10:00:00&enddt=2026-04-10T18:00:00&body=Join%20us%20as%20we%20celebrate%20our%20love%20and%20union.%0A%0ACeremony%3A%2010%3A00%20AM%0AReception%3A%2012%3A00%20NN%0ADress%20Code%3A%20Formal%20%2F%20Semi-Formal&location=Daluyan%20Community%20Resource%20Center%2C%20Sitio%20Acacia%2C%20Brgy.%20Malainen%20Bago%2C%20Naic%2C%20Philippines"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="calendar-btn outlook-cal"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="cal-icon">📧</span>
                          <span>Outlook</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* Page 9 - Final Message */}
                <Page className="page-cream">
                  <div className="page-content final-page">
                    <div className="final-message">
                      <p className="thank-you">Thank you for being part</p>
                      <p className="thank-you">of our special day!</p>
                      <div className="final-couple">
                        <span className="final-names">{invitationData.groomName} & {invitationData.brideName}</span>
                      </div>
                      <p className="final-date">05.09.2026</p>
                      <div className="final-hearts">💕</div>
                    </div>
                  </div>
                </Page>
              </HTMLFlipBook>
              </div>
            </div>

            {/* Swipe hint for mobile - positioned below the booklet */}
            {isMobile && (
              <div className="swipe-hint">
                ← Swipe to turn pages →
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Boarding Pass - Horizontal Offcanvas from Bottom */}
      <div className={`boarding-pass-offcanvas ${showBoardingPass ? 'show' : ''}`}>
        {/* Hand gesture hint overlay */}
        {showBoardingPassHint && showBoardingPass && (
          <div 
            className="bp-hint-overlay" 
            onClick={() => setShowBoardingPassHint(false)}
            onTouchStart={(e) => {
              e.currentTarget.dataset.touchStartY = e.touches[0].clientY;
            }}
            onTouchMove={(e) => {
              const startY = parseFloat(e.currentTarget.dataset.touchStartY);
              const currentY = e.touches[0].clientY;
              // If swiped down more than 30px, hide the hint
              if (currentY - startY > 30) {
                setShowBoardingPassHint(false);
              }
            }}
          >
            <div className="bp-hint-gesture">
              <div className="bp-hint-hand">👆</div>
              <div className="bp-hint-text">Tap or swipe down to hide & navigate</div>
              <div className="bp-tap-rings">
                <div className="bp-tap-ring ring-1"></div>
                <div className="bp-tap-ring ring-2"></div>
                <div className="bp-tap-ring ring-3"></div>
              </div>
            </div>
          </div>
        )}
        <div className="boarding-pass-handle" onClick={() => { setShowBoardingPass(!showBoardingPass); setShowBoardingPassHint(false); }}>
          <div className="handle-bar"></div>
          <span className="handle-text">{showBoardingPass ? 'Hide' : 'Show'} Boarding Pass</span>
        </div>
        
        <div className="boarding-pass-content">
          {/* Left Section */}
          <div className="bp-left">
            <div className="bp-love-tag">❤️ LOVE IS IN THE AIR ❤️</div>
            
            <div className="bp-save-date">
              <span className="bp-save">Save</span>
              <span className="bp-the-date">the date</span>
            </div>

            <div className="bp-info-grid">
              <div className="bp-info-item">
                <span className="bp-label">CEREMONY</span>
                <span className="bp-value">{invitationData.ceremonyTime}</span>
              </div>
              <div className="bp-info-item">
                <span className="bp-label">RECEPTION</span>
                <span className="bp-value">{invitationData.receptionTime}</span>
              </div>
              <div className="bp-info-item">
                <span className="bp-label">DEPARTURE DATE</span>
                <span className="bp-value-bold">{invitationData.weddingDay}</span>
                <span className="bp-value">{invitationData.weddingDate}</span>
              </div>
            </div>

            <div className="bp-venues">
              <div className="bp-venue">
                <span className="bp-venue-label">VENUE</span>
                <span className="bp-venue-name">{invitationData.churchName}</span>
                <span className="bp-venue-loc">{invitationData.churchLocation}</span>
                <span className="bp-venue-loc">{invitationData.receptionLocation}</span>
              </div>
            </div>

            <div className="bp-initials-box">
              <span>{invitationData.groomName.charAt(0)}</span>
              <span className="bp-heart">💕</span>
              <span>{invitationData.brideName.charAt(0)}</span>
            </div>
          </div>

          {/* Perforation */}
          <div className="bp-perforation"></div>

          {/* Right Section */}
          <div className="bp-right">
            <div className="bp-header">
              <span className="bp-boarding-text">BOARDING PASS</span>
              <span className="bp-always">✈️ Always</span>
            </div>

            <div className="bp-invitation-text">
              <p>PLEASE JOIN IN OUR GREAT ADVENTURE</p>
            </div>

            <div className="bp-event-box">
              <div className="bp-ceremony">
                <span className="bp-ceremony-label">Ceremony</span>
                <span className="bp-ceremony-time">{invitationData.ceremonyTime}</span>
              </div>
              <div className="bp-date-display">
                <span className="bp-month">May</span>
                <span className="bp-day">9</span>
                <span className="bp-year">2026</span>
              </div>
            </div>

            <div className="bp-dress-code">
              <span className="bp-dress-label">DRESS CODE</span>
              <span className="bp-dress-value">{invitationData.dressCode}</span>
            </div>

            <button 
              className="bp-rsvp-btn"
              onClick={() => setShowRsvp(true)}
            >
              RSVP Now
            </button>
          </div>
        </div>
      </div>

      {/* RSVP Modal */}
      {showRsvp && (
        <div className="rsvp-modal-overlay" onClick={() => setShowRsvp(false)}>
          <div className="rsvp-modal" onClick={(e) => e.stopPropagation()}>
            {!rsvpSubmitted ? (
              <>
                <h2>RSVP</h2>
                <p>We would be honored to have you celebrate with us!</p>
                
                <form onSubmit={handleRsvpSubmit}>
                  <div className="rsvp-form-group">
                    <label>Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={rsvpData.name}
                      onChange={handleRsvpChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="rsvp-form-group">
                    <label>Will you attend?</label>
                    <div className="rsvp-options">
                      <label className={`rsvp-option ${rsvpData.attending === 'yes' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="attending"
                          value="yes"
                          checked={rsvpData.attending === 'yes'}
                          onChange={handleRsvpChange}
                          required
                        />
                        <span>🎉 Joyfully Accept</span>
                      </label>
                      <label className={`rsvp-option ${rsvpData.attending === 'no' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="attending"
                          value="no"
                          checked={rsvpData.attending === 'no'}
                          onChange={handleRsvpChange}
                        />
                        <span>😢 Regretfully Decline</span>
                      </label>
                    </div>
                  </div>

                  {rsvpData.attending === 'yes' && (
                    <div className="rsvp-form-group">
                      <label>Number of Guests</label>
                      <select
                        name="guests"
                        value={rsvpData.guests}
                        onChange={handleRsvpChange}
                      >
                        <option value="1">1 Guest</option>
                        <option value="2">2 Guests</option>
                        <option value="3">3 Guests</option>
                        <option value="4">4 Guests</option>
                        <option value="5">5+ Guests</option>
                      </select>
                    </div>
                  )}

                  <div className="rsvp-form-group">
                    <label>Message for the Couple (Optional)</label>
                    <textarea
                      name="message"
                      value={rsvpData.message}
                      onChange={handleRsvpChange}
                      placeholder="Write your wishes..."
                      rows="3"
                    />
                  </div>

                  {rsvpError && (
                    <div className="rsvp-error">
                      {rsvpError}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="rsvp-submit"
                    disabled={rsvpLoading}
                  >
                    {rsvpLoading ? 'Sending...' : 'Send RSVP'}
                  </button>
                </form>
              </>
            ) : (
              <div className="rsvp-success">
                <div className="success-icon">💌</div>
                <h2>Thank You!</h2>
                <p>Your RSVP has been received.</p>
                <p className="success-name">{rsvpData.name}</p>
                {rsvpData.attending === 'yes' ? (
                  <p>We're excited to celebrate with you! 🎉</p>
                ) : (
                  <p>We'll miss you! Thank you for letting us know. 💕</p>
                )}
                <button onClick={() => setShowRsvp(false)} className="rsvp-close-btn">
                  Close
                </button>
              </div>
            )}
            
            <button className="rsvp-modal-close" onClick={() => setShowRsvp(false)}>✕</button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="invitation-watermark">
        <p>Created with ❤️ by</p>
        <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer" className="logica-brand">
          <img src={logicaLogo} alt="Logica" className="logica-logo" />
          <span>Logica Technology</span>
        </a>
      </div>
    </div>
  );
};

export default PassportWedding;
